import { registerPlugin } from "@capacitor/core";
import { isNativeApp } from "@/lib/runtime";

const CsvExport = registerPlugin("CsvExport");

const STATUS_LABELS = {
  completed: "OK",
  excluded: "Utesluten",
  X: "Utesluten",
  M: "Tvåminutersuteslutning",
  N: "Ej start",
  R: "Brutit",
  FX: "Utesluten",
  F: "Fall",
  FN: "Fall, ej vållande",
  TT: "Tapes",
};

function csvCell(value) {
  const text = value == null ? "" : String(value);
  return /[;"\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function csvRow(...values) {
  return values.map(csvCell).join(";");
}

function formatMatchDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("sv-SE", {
      year: "2-digit",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(date).map((part) => [part.type, part.value])
  );
  return `${parts.day}-${parts.month}-${parts.year}`;
}

function formatMatchTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("sv-SE", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function normalizeFilenamePart(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();
}

function getResultForRider(heat, rider) {
  return (heat?.results || []).find(
    (result) => String(result?.rider_id || "") === String(rider?.rider_id || "")
  );
}

function getTeamLabel(team, homeTeam, awayTeam) {
  if (team === "home") return homeTeam;
  if (team === "away") return awayTeam;
  return team || "";
}

function getStatusLabel(status) {
  if (!status) return "";
  return STATUS_LABELS[status] || status;
}

function sumHeatPoints(rows, team) {
  return rows.reduce((total, row) => {
    if (row.rider?.team !== team) return total;
    return total + Number(row.result?.points || 0);
  }, 0);
}

export function buildProtocolCsv(savedMatch) {
  const match = savedMatch?.match_details || {};
  const results = savedMatch?.user_results || {};
  const homeTeam = match.home_team || "Hemmalag";
  const awayTeam = match.away_team || "Bortalag";
  const lines = [
    csvRow("LAPSTR MATCHPROTOKOLL", "", "", "", "", ""),
    csvRow("Match", `${homeTeam} - ${awayTeam}`, "", "", "", ""),
    csvRow("Datum", formatMatchDate(match.date), "Tid", formatMatchTime(match.date), "", ""),
    csvRow(
      "Slutresultat",
      `${homeTeam} ${results.home_score ?? 0} - ${results.away_score ?? 0} ${awayTeam}`,
      "",
      "",
      "",
      ""
    ),
  ];

  [...(results.heats || [])]
    .sort((a, b) => Number(a?.heat_number || 0) - Number(b?.heat_number || 0))
    .forEach((heat) => {
      const riderRows = Object.entries(heat?.riders || {})
        .sort(([gateA], [gateB]) => Number(gateA) - Number(gateB))
        .map(([gate, rider]) => ({ gate, rider, result: getResultForRider(heat, rider) }));
      const homePoints = sumHeatPoints(riderRows, "home");
      const awayPoints = sumHeatPoints(riderRows, "away");

      lines.push("");
      lines.push(csvRow(
        `HEAT ${heat?.heat_number ?? ""}`,
        heat?.heat_time ? `Heattid ${heat.heat_time}` : "",
        "",
        "",
        "",
        ""
      ));
      lines.push(csvRow("Bana", "Förare", "Lag", "Poäng", "Bonus", "Status"));

      riderRows.forEach(({ gate, rider, result }) => {
        lines.push(csvRow(
          gate,
          rider?.name || result?.rider_name || result?.name || "Okänd förare",
          getTeamLabel(rider?.team, homeTeam, awayTeam),
          result?.points ?? "",
          result?.bonus_points ?? 0,
          getStatusLabel(result?.status)
        ));
      });

      lines.push(csvRow(
        "Heatresultat",
        `${homeTeam} ${homePoints} - ${awayPoints} ${awayTeam}`,
        "",
        "",
        "",
        ""
      ));
    });

  return lines.join("\r\n");
}

export function getProtocolCsvFilename(savedMatch) {
  const match = savedMatch?.match_details || {};
  const teams = [match.home_team, match.away_team]
    .map(normalizeFilenamePart)
    .filter(Boolean)
    .join("-mot-") || "matchprotokoll";
  const date = match.date ? String(match.date).slice(0, 10) : "utan-datum";
  return `lapstr-${teams}-${date}.csv`;
}

function createProtocolCsv(savedMatch) {
  return {
    csv: `\uFEFF${buildProtocolCsv(savedMatch)}`,
    filename: getProtocolCsvFilename(savedMatch),
  };
}

function downloadInBrowser(csv, filename) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

export async function saveProtocolCsv(savedMatch) {
  const { csv, filename } = createProtocolCsv(savedMatch);

  if (!isNativeApp()) {
    downloadInBrowser(csv, filename);
    return { filename, location: "Hämtade filer" };
  }

  return CsvExport.save({ filename, data: csv });
}

export async function shareProtocolCsv(savedMatch) {
  const { csv, filename } = createProtocolCsv(savedMatch);

  if (!isNativeApp()) {
    const file = new File([csv], filename, { type: "text/csv;charset=utf-8" });
    if (navigator.share && navigator.canShare?.({ files: [file] })) {
      await navigator.share({ title: "Matchprotokoll", files: [file] });
      return { filename };
    }
    downloadInBrowser(csv, filename);
    return { filename };
  }

  const [{ Filesystem, Directory, Encoding }, { Share }] = await Promise.all([
    import("@capacitor/filesystem"),
    import("@capacitor/share"),
  ]);
  const file = await Filesystem.writeFile({
    path: filename,
    data: csv,
    directory: Directory.Cache,
    encoding: Encoding.UTF8,
  });
  await Share.share({
    title: "Exportera matchprotokoll",
    text: "Matchprotokoll exporterat från Lapstr.",
    files: [file.uri],
    dialogTitle: "Dela matchprotokoll",
  });
  return { filename };
}
